import { AcknowledgePayload } from '@electron/main/ipcBridge/ipcInboundPayloads';
import { IPCEventType } from '@electron/shared/ipc.types';
import {
  type ShowMeetingStatusWindowOption,
  type MeetingNotificationFilter,
  type MeetingReminderLeadTime,
  type MeetingMenubarIncludeEventsOption,
  type MeetingMenubarPreviewLeadTimeOption,
  type StartNoteTakingPayload,
  MeetingAppsUpdatedPayload,
  MeetingParticipantsUpdatedPayloadSchema,
  MeetingSpeakerNameResolvedPayloadSchema,
  MeetingSegmentEventPayload,
  MeetingIdPayloadSchema,
  MeetingCalendarEventRequestPayloadSchema,
  MeetingRequestStopNotePayloadSchema,
  type StopNoteTakingReason,
  type StopNoteTakingTelemetry,
  type MeetingHeartbeatHealth,
  MeetingHeartbeatHealthSchema,
  type LocalTranscriptionReadiness,
  LocalTranscriptionReadinessSchema,
  TranscriptDeltaPayloadSchema,
  TranscriptionEngineSchema,
  MeetingPostProcessingStateChangedPayloadSchema,
  MeetingPostProcessingSyncStatePayloadSchema,
  MeetingRecoverablePostProcessingRecordingsPayloadSchema,
  type TranscriptionEngine,
  type MeetingAutoStartEventType,
  type MeetingAutoStartMetadata,
  type MeetingRecordingHealthEvent,
  type CalendarEventFilterSettings,
  type MeetingPostProcessingJobReferencePayload,
  type MeetingPostProcessingStartPayload,
  type MeetingPostProcessingSyncStatePayload,
  type MeetingRecoverablePostProcessingRecordingsPayload,
  type MeetingAssistBadge,
  type MeetingAssistPopup,
  type MeetingAssistPopupPresentation,
  MeetingAssistPopupPresentationSchema,
  type MeetingAssistPopupResolution,
  MeetingAssistPopupResolutionSchema,
  type MeetingAssistPopupView,
  MeetingAssistPopupViewSchema,
  MeetingWidgetShortcutTogglePayloadSchema,
  type MeetingWidgetShortcutTogglePayload,
  type MeetingWidgetShortcutUpdateResult,
} from '@electron/shared/meetings.types';

import {
  type StartMeetingInput,
  type MeetingPermissions,
  MeetingAudioEnergyLevelsPayload,
  type MeetingId,
  type MeetingPermissionType,
  MeetingNavigatePayload,
  MeetingStateChangePayload,
  type MeetingStatusWindowMessage,
  MeetingWindowFocusChangePayload,
} from '@/features/meetings/meetings.types';
import { AppCapability, isCapable } from '@/utils';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronListenerMethod } from '../base/types';

import { NativeStartRejectedError } from './NativeStartRejectedError';

export {
  ShowMeetingStatusWindowOption,
  MeetingNotificationFilter,
  MeetingReminderLeadTime,
  MeetingMenubarIncludeEventsOption,
  MeetingMenubarPreviewLeadTimeOption,
} from '@electron/shared/meetings.types';

const Commands = {
  ClearStaleMeetingState: 'clear-stale-meeting-state',
  MeetingStatusShowWindow: 'show-meeting-status-window',
  MeetingStatusWindowDismiss: 'dismiss-meeting-status-window',
  MeetingStatusWindowHide: 'hide-meeting-status-window',
  MeetingStatusWindowResign: 'resign-meeting-status-window',
  MeetingStatusWindowReady: 'meeting-status-window-ready',
  GetMacPermissions: 'meeting-get-mac-permissions',
  RequestMacPermissions: 'meeting-request-mac-permissions',
  GetShowMeetingStatusWindowOption:
    'meeting-get-show-meeting-status-window-option',
  SetShowMeetingStatusWindowOption:
    'meeting-set-show-meeting-status-window-option',
  SetHideWidgetFromScreenShare: 'meeting-set-hide-widget-from-screen-share',
  GetMeetingNotificationFilter: 'meeting-get-notification-filter',
  SetMeetingNotificationFilter: 'meeting-set-notification-filter',
  GetMeetingReminderLeadTime: 'meeting-get-reminder-lead-time',
  SetMeetingReminderLeadTime: 'meeting-set-reminder-lead-time',
  GetDisableMeetingReminders: 'meeting-get-disable-reminders',
  SetDisableMeetingReminders: 'meeting-set-disable-reminders',
  GetExcludeSoloEvents: 'meeting-get-exclude-solo-events',
  SetExcludeSoloEvents: 'meeting-set-exclude-solo-events',
  SetCalendarEventFilters: 'meeting-set-calendar-event-filters',
  GetMenubarCalendarEnabled: 'meeting-get-menubar-calendar-enabled',
  SetMenubarCalendarEnabled: 'meeting-set-menubar-calendar-enabled',
  GetMenubarIncludeEvents: 'meeting-get-menubar-include-events',
  SetMenubarIncludeEvents: 'meeting-set-menubar-include-events',
  GetMenubarPreviewLeadTime: 'meeting-get-menubar-preview-lead-time',
  SetMenubarPreviewLeadTime: 'meeting-set-menubar-preview-lead-time',
  GetDisableMeetingDetectionNotification:
    'meeting-get-disable-detection-notification',
  SetDisableMeetingDetectionNotification:
    'meeting-set-disable-detection-notification',
  RequestStartMeetingNote: 'request-start-meeting-note',
  RequestStopMeetingNote: 'request-stop-meeting-note',
  LogAutoStartEvent: 'log-meeting-auto-start-event',
  LogRecordingHealthEvent: 'log-meeting-recording-health-event',
  NotifyMeetingStatusUpdate: 'notify-meeting-status-update',
  NotifyMainWindowStateChanged: 'notify-main-window-state-changed',
  SetMeetingStatusWindowAppearance: 'set-meeting-status-window-appearance',
  SetMeetingAssistBadge: 'set-meeting-assist-badge',
  SetMeetingAssistPopup: 'set-meeting-assist-popup',
  ResolveMeetingAssistPopup: 'resolve-meeting-assist-popup',
  SetMeetingStatusWindowPinned: 'set-meeting-status-window-pinned',
  GetActiveMeetingId: 'get-current-meeting-state',
  GetDetectedMeetingApps: 'get-detected-meeting-apps',
  RequestActiveMeetingResend: 'request-active-meeting-resend',
  NotifyMeetingStarted: 'notify-meeting-started',
  MarkUpcomingMeetingJoined: 'mark-upcoming-meeting-joined',
  OpenMeetingChat: 'open-meeting-chat',
} as const;

export class MeetingNotesAPI extends ElectronAPI {
  getMeetingWidgetShortcut(): Promise<string> {
    return this.invoke<string>('meeting-get-widget-shortcut');
  }

  setMeetingWidgetShortcut(
    shortcut: string
  ): Promise<MeetingWidgetShortcutUpdateResult> {
    return this.invoke<MeetingWidgetShortcutUpdateResult>(
      'meeting-set-widget-shortcut',
      { shortcut }
    );
  }

  setMeetingWidgetShortcutSuspended(suspended: boolean): Promise<void> {
    return this.invoke<void>('meeting-set-widget-shortcut-suspended', {
      suspended,
    });
  }

  listenMeetingWidgetShortcutToggle(
    callback: (payload: MeetingWidgetShortcutTogglePayload) => void
  ): () => void {
    return this.addListener({
      eventType: 'meeting-status-window-shortcut-toggle',
      callback,
      payloadValidator: MeetingWidgetShortcutTogglePayloadSchema,
    });
  }

  /** Starts a meeting - throws if Swift returns an error or malformed active engine ack */
  async startMeeting(
    payload: StartNoteTakingPayload
  ): Promise<TranscriptionEngine> {
    const response = await this.requestContextKit(
      IPCEventType.startNoteTaking,
      payload
    );

    if (response?.type === 'error') {
      throw new NativeStartRejectedError(
        response.info ?? 'Failed to start meeting'
      );
    }

    if (response?.type !== 'success') {
      throw new Error(
        'Failed to start meeting: missing ContextKit acknowledgement'
      );
    }

    const parsedActiveTranscriptionEngine = TranscriptionEngineSchema.safeParse(
      response.info
    );
    if (!parsedActiveTranscriptionEngine.success) {
      throw new Error(
        `Invalid start meeting response: expected active transcription engine "remote" or "local", received ${response.info ?? 'missing'}`
      );
    }

    return parsedActiveTranscriptionEngine.data;
  }

  /** Stops the current meeting - throws if Swift returns an error */
  async stopMeeting(
    reason: StopNoteTakingReason,
    telemetry?: StopNoteTakingTelemetry
  ) {
    const response = await this.requestContextKit(IPCEventType.stopNoteTaking, {
      reason,
      ...telemetry,
    });

    if (response?.type === 'error') {
      // "No meeting in progress" is an expected no-op case (invariant logged on Swift side)
      if (response.info === 'NO_MEETING_IN_PROGRESS') {
        return;
      }
      throw new Error(response.info ?? 'Failed to stop meeting');
    }
  }

  /** Pings ContextKit to verify it's responsive and keep the session alive */
  async meetingHeartbeat(): Promise<MeetingHeartbeatHealth> {
    const response = await this.requestContextKit(
      IPCEventType.meetingHeartbeat
    );

    if (response?.type === 'error' || !response?.info) {
      throw new Error(response?.info ?? 'Heartbeat failed');
    }

    const parsed = MeetingHeartbeatHealthSchema.safeParse(
      JSON.parse(response.info)
    );
    if (!parsed.success) {
      throw new Error(`Invalid heartbeat response: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  async getLocalTranscriptionReadiness(): Promise<LocalTranscriptionReadiness> {
    return this.requestLocalTranscriptionReadiness(
      IPCEventType.meetingLocalTranscriptionReadinessGet
    );
  }

  async retryLocalTranscriptionReadiness(): Promise<LocalTranscriptionReadiness> {
    return this.requestLocalTranscriptionReadiness(
      IPCEventType.meetingLocalTranscriptionReadinessRetry
    );
  }

  async startPostProcessing(
    payload: MeetingPostProcessingStartPayload
  ): Promise<void> {
    const response = await this.requestContextKit(
      IPCEventType.meetingPostProcessingStart,
      payload
    );

    if (response?.type === 'error') {
      throw new Error(
        response.info ?? 'Meeting Post-Processing request failed'
      );
    }

    if (response?.type !== 'success') {
      throw new Error(
        'Meeting Post-Processing start is missing a ContextKit acknowledgement'
      );
    }
  }

  async getRecoverablePostProcessingRecordings(): Promise<MeetingRecoverablePostProcessingRecordingsPayload> {
    const response = await this.requestContextKit(
      IPCEventType.meetingPostProcessingRecoverableRecordings
    );

    if (response?.type === 'error' || !response?.info) {
      throw new Error(
        response?.info ??
          'Meeting Post-Processing recoverable recordings request failed'
      );
    }

    const parsed =
      MeetingRecoverablePostProcessingRecordingsPayloadSchema.safeParse(
        JSON.parse(response.info)
      );
    if (!parsed.success) {
      throw new Error(
        `Invalid recoverable Meeting Post-Processing response: ${parsed.error.message}`
      );
    }
    return parsed.data;
  }

  /** Confirms the renderer durably persisted a terminal post-processing
   * result; ContextKit deletes the retained audio artifacts only after this. */
  async acknowledgePostProcessingResult(
    payload: MeetingPostProcessingJobReferencePayload
  ): Promise<void> {
    const response = await this.requestContextKit(
      IPCEventType.meetingPostProcessingAcknowledgeResult,
      payload
    );

    if (response?.type !== 'success') {
      throw new Error(
        response?.info ??
          'Meeting Post-Processing result acknowledgement failed'
      );
    }
  }

  async getPostProcessingJobState(
    payload: MeetingPostProcessingJobReferencePayload
  ): Promise<MeetingPostProcessingSyncStatePayload> {
    const response = await this.requestContextKit(
      IPCEventType.meetingPostProcessingSyncState,
      payload
    );

    if (response?.type === 'error' || !response?.info) {
      throw new Error(
        response?.info ?? 'Meeting Post-Processing job state request failed'
      );
    }

    const parsed = MeetingPostProcessingSyncStatePayloadSchema.safeParse(
      JSON.parse(response.info)
    );
    if (!parsed.success) {
      throw new Error(
        `Invalid Meeting Post-Processing job state response: ${parsed.error.message}`
      );
    }
    return parsed.data;
  }

  private async requestLocalTranscriptionReadiness(
    eventType:
      | IPCEventType.meetingLocalTranscriptionReadinessGet
      | IPCEventType.meetingLocalTranscriptionReadinessRetry
  ): Promise<LocalTranscriptionReadiness> {
    const response = await this.requestContextKit(eventType);

    if (response?.type === 'error' || !response?.info) {
      throw new Error(response?.info ?? 'Local Transcription readiness failed');
    }

    const parsed = LocalTranscriptionReadinessSchema.safeParse(
      JSON.parse(response.info)
    );
    if (!parsed.success) {
      throw new Error(
        `Invalid Local Transcription readiness response: ${parsed.error.message}`
      );
    }
    return parsed.data;
  }

  /** Shows the meeting status window */
  async showMeetingStatusWindow(meetingId: MeetingId) {
    return this.invoke<void>(Commands.MeetingStatusShowWindow, {
      meetingId,
    });
  }

  /** Dismisses the meeting status window */
  async dismissMeetingStatusWindow() {
    return this.invoke<void>(Commands.MeetingStatusWindowDismiss);
  }

  /** Hides the meeting status window */
  async hideMeetingStatusWindow() {
    return this.invoke<void>(Commands.MeetingStatusWindowHide);
  }

  /** Resigns key status from the meeting status window */
  async resignMeetingStatusWindow() {
    return this.invoke<void>(Commands.MeetingStatusWindowResign);
  }

  /** Notifies main process that status window is ready */
  async notifyStatusWindowReady() {
    return this.invoke<void>(Commands.MeetingStatusWindowReady);
  }

  /** Sets the appearance of the meeting status window */
  async setMeetingStatusWindowAppearance(
    appearance: 'expanded' | 'collapsed',
    focus = false
  ) {
    return this.invoke<void>(Commands.SetMeetingStatusWindowAppearance, {
      appearance,
      ...(focus ? { focus: true } : {}),
    });
  }

  /** Shows or hides the Meeting Assist badge over the collapsed widget */
  async setMeetingAssistBadge(badge: MeetingAssistBadge) {
    return this.invoke<void>(Commands.SetMeetingAssistBadge, badge);
  }

  /** Pops a Meeting Assist card up beside the collapsed widget, or takes the popup down with a null card */
  async setMeetingAssistPopup(popup: MeetingAssistPopup) {
    return this.invoke<void>(Commands.SetMeetingAssistPopup, popup);
  }

  /** From the popup's own window: it is ready for its first card */
  meetingAssistPopupReady(): void {
    this.ipcRenderer?.send('meeting-assist-popup-ready');
  }

  /** From the popup's own window: how the popup ended */
  async resolveMeetingAssistPopup(resolution: MeetingAssistPopupResolution) {
    return this.invoke<void>(Commands.ResolveMeetingAssistPopup, resolution);
  }

  /** In the popup's own window: the card to show, or null, and whether the window is on screen */
  listenMeetingAssistPopupChanged(
    callback: (view: MeetingAssistPopupView) => void
  ): () => void {
    return this.addListener({
      eventType: 'meeting-assist-popup-changed',
      callback,
      payloadValidator: MeetingAssistPopupViewSchema,
    });
  }

  /** In the meeting widget: a card its popup showed on screen for the first time */
  listenMeetingAssistPopupPresented(
    callback: (presentation: MeetingAssistPopupPresentation) => void
  ): () => void {
    return this.addListener({
      eventType: 'meeting-assist-popup-presented',
      callback,
      payloadValidator: MeetingAssistPopupPresentationSchema,
    });
  }

  /** In the meeting widget: how its popup ended */
  listenMeetingAssistPopupResolved(
    callback: (resolution: MeetingAssistPopupResolution) => void
  ): () => void {
    return this.addListener({
      eventType: 'meeting-assist-popup-resolved',
      callback,
      payloadValidator: MeetingAssistPopupResolutionSchema,
    });
  }

  /** Sets whether the meeting status window is pinned */
  async setMeetingStatusWindowPinned(isPinned: boolean) {
    return this.invoke<void>(Commands.SetMeetingStatusWindowPinned, {
      isPinned,
    });
  }

  /** Clears stale meeting state in the main process when the renderer has no active meeting */
  async clearStaleMeetingState(): Promise<void> {
    return this.invoke<void>(Commands.ClearStaleMeetingState);
  }

  /** Gets the active meeting ID, or null if no meeting is active */
  async getActiveMeetingId(): Promise<string | null> {
    return this.invoke<string | null>(Commands.GetActiveMeetingId);
  }

  async getDetectedMeetingApps() {
    const report = await this.invoke<unknown>(Commands.GetDetectedMeetingApps);
    return MeetingAppsUpdatedPayload.nullable().parse(report ?? null);
  }

  /**
   * Ask the main window to re-send the active meeting via meetingStateDidChange.
   * Fire-and-forget — the main window decides whether a meeting is active.
   * The MSW picks up the response through its listenMeetingStateDidChange
   * handler, which also initializes recording state if needed.
   */
  async requestActiveMeetingResend(): Promise<void> {
    return this.invoke<void>(Commands.RequestActiveMeetingResend);
  }

  /** Notifies main that a meeting started; an event id also suppresses that event's reminder. */
  async notifyMeetingStarted(
    meetingId: string,
    eventId?: string
  ): Promise<void> {
    return this.invoke<void>(Commands.NotifyMeetingStarted, {
      meetingId,
      eventId,
    });
  }

  async markUpcomingMeetingJoined(eventId: string): Promise<void> {
    return this.invoke<void>(Commands.MarkUpcomingMeetingJoined, { eventId });
  }

  /** Gets current macOS permissions status */
  async getMacPermissions(): Promise<MeetingPermissions | undefined> {
    return this.invoke<MeetingPermissions>(Commands.GetMacPermissions);
  }

  /** Requests specific macOS permissions */
  async requestMacPermissions(
    permissions: MeetingPermissionType[]
  ): Promise<MeetingPermissions> {
    return this.invoke<MeetingPermissions>(Commands.RequestMacPermissions, {
      permissions,
    });
  }

  /** Gets the current show meeting status window option */
  async getShowMeetingStatusWindowOption(): Promise<ShowMeetingStatusWindowOption> {
    return this.invoke<ShowMeetingStatusWindowOption>(
      Commands.GetShowMeetingStatusWindowOption
    );
  }

  /** Sets the show meeting status window option */
  async setShowMeetingStatusWindowOption(
    option: ShowMeetingStatusWindowOption
  ): Promise<void> {
    return this.invoke<void>(Commands.SetShowMeetingStatusWindowOption, {
      option,
    });
  }

  /** Sets whether the meeting widget is hidden from screen capture/sharing */
  async setHideMeetingWidgetFromScreenShare(hidden: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetHideWidgetFromScreenShare, {
      hidden,
    });
  }

  /** Gets the current meeting notification filter */
  async getMeetingNotificationFilter(): Promise<MeetingNotificationFilter> {
    return this.invoke<MeetingNotificationFilter>(
      Commands.GetMeetingNotificationFilter
    );
  }

  /** Sets the meeting notification filter */
  async setMeetingNotificationFilter(
    filter: MeetingNotificationFilter
  ): Promise<void> {
    return this.invoke<void>(Commands.SetMeetingNotificationFilter, {
      filter,
    });
  }

  /** Gets the current meeting reminder lead time */
  async getMeetingReminderLeadTime(): Promise<MeetingReminderLeadTime> {
    return this.invoke<MeetingReminderLeadTime>(
      Commands.GetMeetingReminderLeadTime
    );
  }

  /** Sets the meeting reminder lead time */
  async setMeetingReminderLeadTime(
    leadTime: MeetingReminderLeadTime
  ): Promise<void> {
    return this.invoke<void>(Commands.SetMeetingReminderLeadTime, {
      leadTime,
    });
  }

  /** Gets whether meeting reminders are disabled */
  async getDisableMeetingReminders(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetDisableMeetingReminders);
  }

  /** Sets whether meeting reminders are disabled */
  async setDisableMeetingReminders(disabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetDisableMeetingReminders, {
      disabled,
    });
  }

  /** Gets whether solo events are excluded from notifications */
  async getExcludeSoloEvents(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetExcludeSoloEvents);
  }

  /** Sets whether solo events are excluded from notifications */
  async setExcludeSoloEvents(exclude: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetExcludeSoloEvents, { exclude });
  }

  /** Sets the calendar event filters (days ahead, max events, excluded types/statuses) the
   * main process's menu-bar calendar poll should send to GET /calendar/events. */
  async setCalendarEventFilters(
    filters: Omit<CalendarEventFilterSettings, 'excludeSoloEvents'>
  ): Promise<void> {
    return this.invoke<void>(Commands.SetCalendarEventFilters, filters);
  }

  /** Gets whether the menu bar calendar is enabled */
  async getMenubarCalendarEnabled(): Promise<boolean> {
    return this.invoke<boolean>(Commands.GetMenubarCalendarEnabled);
  }

  /** Sets whether the menu bar calendar is enabled */
  async setMenubarCalendarEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(Commands.SetMenubarCalendarEnabled, { enabled });
  }

  /** Gets the event range included in the menu bar calendar */
  async getMenubarIncludeEvents(): Promise<MeetingMenubarIncludeEventsOption> {
    return this.invoke<MeetingMenubarIncludeEventsOption>(
      Commands.GetMenubarIncludeEvents
    );
  }

  /** Sets the event range included in the menu bar calendar */
  async setMenubarIncludeEvents(
    option: MeetingMenubarIncludeEventsOption
  ): Promise<void> {
    return this.invoke<void>(Commands.SetMenubarIncludeEvents, { option });
  }

  /** Gets the preview lead time used for the menu bar chip */
  async getMenubarPreviewLeadTime(): Promise<MeetingMenubarPreviewLeadTimeOption> {
    return this.invoke<MeetingMenubarPreviewLeadTimeOption>(
      Commands.GetMenubarPreviewLeadTime
    );
  }

  /** Sets the preview lead time used for the menu bar chip */
  async setMenubarPreviewLeadTime(
    option: MeetingMenubarPreviewLeadTimeOption
  ): Promise<void> {
    return this.invoke<void>(Commands.SetMenubarPreviewLeadTime, { option });
  }

  /** Gets whether meeting detection notifications are disabled */
  async getDisableMeetingDetectionNotification(): Promise<boolean> {
    return this.invoke<boolean>(
      Commands.GetDisableMeetingDetectionNotification
    );
  }

  /** Sets whether meeting detection notifications are disabled */
  async setDisableMeetingDetectionNotification(
    disabled: boolean
  ): Promise<void> {
    return this.invoke<void>(Commands.SetDisableMeetingDetectionNotification, {
      disabled,
    });
  }

  /** Sets the app bundle keys excluded from meeting detection */
  setMeetingDetectionDisabledApps(disabledBundleKeys: string[]): void {
    this.notifyContextKit(IPCEventType.setMeetingDetectionDisabledApps, {
      disabledBundleKeys,
    });
  }

  /** Sets the website hosts excluded from browser meeting detection */
  setMeetingDetectionDisabledSites(disabledHosts: string[]): void {
    this.notifyContextKit(IPCEventType.setMeetingDetectionDisabledSites, {
      disabledHosts,
    });
  }

  /** Requests main window to start a meeting note (from status window) */
  async requestStartMeetingNote(payload: StartMeetingInput): Promise<void> {
    return this.invoke<void>(Commands.RequestStartMeetingNote, payload);
  }

  /** Fire-and-forget meeting_auto_start telemetry; swallows IPC errors so telemetry never destabilizes the renderer. */
  logAutoStartEvent<T extends MeetingAutoStartEventType>(
    eventType: T,
    metadata: MeetingAutoStartMetadata<T>
  ): void {
    this.invoke<void>(Commands.LogAutoStartEvent, {
      event_type: eventType,
      ...metadata,
    }).catch(() => {
      // Intentionally swallowed.
    });
  }

  /** Fire-and-forget meeting_recording_health telemetry; swallows IPC errors like logAutoStartEvent. */
  logRecordingHealthEvent(event: MeetingRecordingHealthEvent): void {
    this.invoke<void>(Commands.LogRecordingHealthEvent, event).catch(() => {
      // Intentionally swallowed.
    });
  }

  /** Requests main window to stop a meeting note */
  async requestStopMeetingNote(): Promise<void> {
    return this.invoke<void>(Commands.RequestStopMeetingNote, {});
  }

  /** Sends a typed meeting status message to the status window */
  async notifyMeetingStatusUpdate(
    message: MeetingStatusWindowMessage
  ): Promise<void> {
    if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return;
    return this.invoke<void>(Commands.NotifyMeetingStatusUpdate, message);
  }

  /** Notifies main window that meeting state has changed from status window */
  async notifyMainWindowStateChanged(
    patch?: Array<{ id: MeetingId; [key: string]: unknown }>
  ): Promise<void> {
    if (isCapable(AppCapability.DesktopMainWindowExclusive)) return;
    return this.invoke<void>(Commands.NotifyMainWindowStateChanged, { patch });
  }

  async openMeetingChat(meetingId: MeetingId): Promise<void> {
    if (isCapable(AppCapability.DesktopMainWindowExclusive)) return;
    return this.invoke<void>(Commands.OpenMeetingChat, { meetingId });
  }

  /** Listens for meeting stopped events */
  listenStopped: ElectronListenerMethod<IPCEventType.meetingStopped> = (
    callback,
    onError,
    onUnlisten,
    timeout,
    once = true
  ) => {
    return this.addListener({
      eventType: IPCEventType.meetingStopped,
      callback,
      payloadValidator: MeetingIdPayloadSchema,
      onError,
      onUnlisten,
      once,
      timeout,
    });
  };

  /** Listens for audio level updates during meetings */
  listenAudioEnergyLevelsReceived: ElectronListenerMethod<IPCEventType.meetingAudioEnergyLevelsReceived> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingAudioEnergyLevelsReceived,
        payloadValidator: MeetingAudioEnergyLevelsPayload,
        callback,
      });
    };

  listenOpenMeetingChat: ElectronListenerMethod<IPCEventType.meetingOpenChat> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingOpenChat,
        callback,
        payloadValidator: MeetingNavigatePayload,
      });
    };

  /** Listens for main window focus change events */
  listenMainWindowFocusChange: ElectronListenerMethod<IPCEventType.meetingMainWindowFocusChange> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingMainWindowFocusChange,
        callback,
        payloadValidator: MeetingWindowFocusChangePayload,
      });
    };

  /** Listens for meeting status window moved events */
  listenStatusWindowMoved: ElectronListenerMethod<IPCEventType.meetingStatusWindowMoved> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingStatusWindowMoved,
        callback,
      });
    };

  /** Listens for meeting status window should collapse events */
  listenStatusWindowShouldCollapse: ElectronListenerMethod<IPCEventType.meetingStatusWindowShouldCollapse> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingStatusWindowShouldCollapse,
        callback,
      });
    };

  /** Listens for request to open or create a quick meeting note */
  listenRequestQuickNote: ElectronListenerMethod<IPCEventType.meetingRequestQuickNote> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingRequestQuickNote,
        callback,
      });
    };

  /** Listens for request to open or create a prep meeting from a calendar event */
  listenRequestPrepCalendarEvent: ElectronListenerMethod<IPCEventType.meetingRequestPrepCalendarEvent> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingRequestPrepCalendarEvent,
        callback,
        payloadValidator: MeetingCalendarEventRequestPayloadSchema,
      });
    };

  /** Listens for request to open or start recording a meeting from a calendar event */
  listenRequestStartCalendarEvent: ElectronListenerMethod<IPCEventType.meetingRequestStartCalendarEvent> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingRequestStartCalendarEvent,
        callback,
        payloadValidator: MeetingCalendarEventRequestPayloadSchema,
      });
    };

  /** Listens for request to start meeting note events */
  listenRequestStartNote: ElectronListenerMethod<IPCEventType.meetingRequestStartNote> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingRequestStartNote,
        callback,
      });
    };

  /** Listens for request to stop meeting note events */
  listenRequestStopNote: ElectronListenerMethod<IPCEventType.meetingRequestStopNote> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingRequestStopNote,
        callback,
        payloadValidator: MeetingRequestStopNotePayloadSchema,
      });
    };

  /** Listens for meeting state change events (rename, delete, etc.) */
  listenMeetingStateDidChange: ElectronListenerMethod<IPCEventType.meetingStateDidChange> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.meetingStateDidChange,
        callback,
        payloadValidator: MeetingStateChangePayload,
      });
    };

  /** Listens for meeting start/stop acknowledgement (fires once, may contain errors) */
  listenForMeetingAcknowledgement: ElectronListenerMethod<IPCEventType.acknowledge> =
    (callback, onError, onUnlisten, timeout, once = true) => {
      return this.addListener({
        eventType: IPCEventType.acknowledge,
        callback,
        onError,
        onUnlisten,
        payloadValidator: AcknowledgePayload,
        once,
        timeout,
      });
    };

  /**
   * Registers a listener for meeting app detection events.
   * Fires when the native layer detects a supported meeting application (e.g., Zoom, Meet).
   * Payload is null; the event itself signals detection.
   */
  listenForMeetingAppsUpdated: ElectronListenerMethod<IPCEventType.meetingAppsUpdated> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingAppsUpdated,
        callback,
        onError,
        onUnlisten,
        payloadValidator: MeetingAppsUpdatedPayload,
        timeout,
      });
    };

  /** Listens for the participant roster ContextKit reads from the meeting app while a note is recording */
  listenForMeetingParticipantsUpdated: ElectronListenerMethod<IPCEventType.meetingParticipantsUpdated> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingParticipantsUpdated,
        callback,
        onError,
        onUnlisten,
        payloadValidator: MeetingParticipantsUpdatedPayloadSchema,
        timeout,
      });
    };

  listenForMeetingSpeakerNameResolved: ElectronListenerMethod<IPCEventType.meetingSpeakerNameResolved> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingSpeakerNameResolved,
        callback,
        onError,
        onUnlisten,
        payloadValidator: MeetingSpeakerNameResolvedPayloadSchema,
        timeout,
      });
    };

  /**
   * Registers a listener for browser meeting-segment boundaries.
   * Fires only during an active recording, when the browser joins, leaves, or
   * moves between meeting rooms.
   */
  listenForMeetingSegmentEvent: ElectronListenerMethod<IPCEventType.meetingSegmentEvent> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingSegmentEvent,
        callback,
        onError,
        onUnlisten,
        payloadValidator: MeetingSegmentEventPayload,
        timeout,
      });
    };

  /** Listens for meeting interrupt events (unrecoverable transcriber failures) */
  listenForMeetingInterrupt: ElectronListenerMethod<IPCEventType.meetingInterrupt> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingInterrupt,
        callback,
        onError,
        onUnlisten,
        timeout,
      });
    };

  listenLocalTranscriptionReadinessChanged: ElectronListenerMethod<IPCEventType.meetingLocalTranscriptionReadinessChanged> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingLocalTranscriptionReadinessChanged,
        callback,
        onError,
        onUnlisten,
        payloadValidator: LocalTranscriptionReadinessSchema,
        timeout,
      });
    };

  listenPostProcessingStateChanged: ElectronListenerMethod<IPCEventType.meetingPostProcessingStateChanged> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingPostProcessingStateChanged,
        callback,
        onError,
        onUnlisten,
        payloadValidator: MeetingPostProcessingStateChangedPayloadSchema,
        timeout,
      });
    };

  /** Listens for quit requests that are blocked by an active meeting */
  listenQuitBlockedByMeeting: ElectronListenerMethod<IPCEventType.quitBlockedByMeeting> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.quitBlockedByMeeting,
        callback,
        onError,
        onUnlisten,
        timeout,
      });
    };

  /**
   * Registers a listener for transcript delta events from CK.
   * Fires when CK sends a new chunk of transcription text.
   */
  listenTranscriptDelta: ElectronListenerMethod<IPCEventType.meetingTranscriptDelta> =
    (callback, onError, onUnlisten, timeout) => {
      return this.addListener({
        eventType: IPCEventType.meetingTranscriptDelta,
        callback,
        onError,
        onUnlisten,
        payloadValidator: TranscriptDeltaPayloadSchema,
        timeout,
      });
    };

  /** Removes a listener for the specified meeting event type */
  removeListener(eventType: IPCEventType): void {
    super.removeListener(eventType);
  }
}
